import moment from "moment";
import { LanguageType } from "../models/app-config";

const isDateValid = (dateStr: string): boolean => {
  return !dateStr || !isNaN(new Date(dateStr)?.getTime());
};

const isTimestampToday = (timestamp: string): boolean => {
  const dateFromTimestamp = new Date(timestamp);
  const today = new Date();

  // Extract date components from the timestamp
  const timestampDate = new Date(
    dateFromTimestamp.getFullYear(),
    dateFromTimestamp.getMonth(),
    dateFromTimestamp.getDate()
  );

  // Extract date components from today's date
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  // Compare if the dates are equal
  return timestampDate.getTime() === todayDate.getTime();
};

const formatConversationDate = (
  date: string,
  isShowHour = true,
  todayText: string,
  yesterdayText: string
): string => {
  if (!isDateValid(date)) return "";
  else if (isTimestampToday(date)) {
    if (isShowHour) {
      return moment(date).format("LT"); // 11:07 AM
    }
    return todayText; // Today
  } else if (moment(date).isSame(moment().subtract(1, "days"), "day")) {
    return yesterdayText; // Yesterday
  } else {
    // Check if the date's year matches the current year
    return moment(date).format("MMM DD"); // Jan 4 (without year)
  }
};

const isDateExpired = (expiredAt: string): boolean => {
  const currentDateTime = moment();
  const expiredDateTime = moment(expiredAt);

  return expiredDateTime.isBefore(currentDateTime);
};

const isSameOrAfter = (date1: string | null, date2: string | null): boolean => {
  if (!date1 || !date2) return false;
  const date1Moment = new Date(date1).getTime();
  const date2Moment = new Date(date2).getTime();

  return date1Moment > date2Moment;
};

/*
  Utility function to convert seconds to plan duration text

  @param seconds - The number of seconds to convert
  @returns A string representing the duration in a human-readable format
  @example
  formatDuration(60) // "1 minute"
  formatDuration(3600) // "1 hour"
  formatDuration(86400) // "1 day"

*/

const formatDurationNumber = (duration: number): string => {
  return Number.isInteger(duration) ? String(duration) : duration.toFixed(2);
};

const convertSecondsToDuration = (
  seconds: number,
  translate: (key: string, options?: { count: string }) => string,
  durationPlans?: Array<{ duration: number; en: string; ja: string }>,
  locale?: string
): string => {
  // If durationPlans and locale are provided, look up the duration in config first
  if (durationPlans && locale) {
    const plan = durationPlans.find((p) => p.duration === seconds);
    if (plan) {
      return locale === LanguageType.JA ? plan.ja : plan.en;
    }
  }

  // Fallback to original translation-based logic
  const duration = moment.duration(seconds, "seconds");
  const days = duration.asDays();
  const minutes = duration.asMinutes();

  if (days >= 1) {
    const formattedDays = formatDurationNumber(days);
    return translate("web.x_days", { count: formattedDays });
  } else if (minutes >= 1) {
    const formattedMinutes = formatDurationNumber(minutes);
    return translate("web.x_minutes", { count: formattedMinutes });
  } else {
    return translate("less_than_1_minute");
  }
};

export {
  isDateValid,
  isTimestampToday,
  formatConversationDate,
  isDateExpired,
  isSameOrAfter,
  convertSecondsToDuration,
};
